import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Trash2 } from "lucide-react";

const fmt = new Intl.NumberFormat("nb-NO", { maximumFractionDigits: 0 });

export default function DebtTool() {
  const [debts, setDebts] = useState([]);
  const [name, setName] = useState("");
  const [balance, setBalance] = useState("");
  const [rate, setRate] = useState("");
  const [strategy, setStrategy] = useState("snoeball");

  const total = useMemo(() => debts.reduce((s, d) => s + d.balance, 0), [debts]);

  const order = useMemo(() => {
    const copy = [...debts];
    copy.sort(strategy === "snoeball" ? (a, b) => a.balance - b.balance : (a, b) => b.rate - a.rate);
    return copy;
  }, [debts, strategy]);

  const add = () => {
    const b = Number(balance);
    if (!name.trim() || !b) return;
    setDebts((d) => [...d, { id: crypto.randomUUID(), name: name.trim(), balance: b, rate: Number(rate) || 0 }]);
    setName(""); setBalance(""); setRate("");
  };

  return (
    <section id="gjeld" data-testid="debt-tool-section" className="py-20 lg:py-28 bg-surface-subtle/60 border-y border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        >
          <p className="text-xs uppercase tracking-[0.2em] font-mono font-semibold text-terracotta">Verktøy 02</p>
          <h2 className="mt-3 font-serif font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl text-primary">
            Gjeldsoversikt
          </h2>
          <p className="mt-4 text-muted-foreground max-w-2xl leading-relaxed">
            Legg inn gjelden din og se hvilken rekkefølge som lønner seg. Tallene forsvinner når du lukker siden.
          </p>
        </motion.div>

        <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-5 bg-surface border border-border rounded-3xl p-6 lg:p-8">
            <h3 className="font-serif font-bold text-xl">Legg til gjeld</h3>
            <div className="mt-5 space-y-4">
              <label className="block">
                <span className="text-xs font-semibold text-muted-foreground">Navn (f.eks. Kredittkort)</span>
                <input data-testid="debt-input-name" value={name} onChange={(e) => setName(e.target.value)}
                  className="mt-1.5 w-full bg-background border border-border rounded-xl px-4 py-2.5 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/15 transition-all duration-200"
                  placeholder="Forbrukslån" />
              </label>
              <div className="grid grid-cols-2 gap-4">
                <label className="block">
                  <span className="text-xs font-semibold text-muted-foreground">Saldo (kr)</span>
                  <input data-testid="debt-input-balance" inputMode="numeric" value={balance}
                    onChange={(e) => setBalance(e.target.value.replace(/[^0-9]/g, ""))}
                    className="mt-1.5 w-full bg-background border border-border rounded-xl px-4 py-2.5 text-sm font-mono outline-none focus:border-primary focus:ring-2 focus:ring-primary/15 transition-all duration-200"
                    placeholder="0" />
                </label>
                <label className="block">
                  <span className="text-xs font-semibold text-muted-foreground">Rente (%)</span>
                  <input data-testid="debt-input-rate" inputMode="decimal" value={rate}
                    onChange={(e) => setRate(e.target.value.replace(/[^0-9.,]/g, ""))}
                    className="mt-1.5 w-full bg-background border border-border rounded-xl px-4 py-2.5 text-sm font-mono outline-none focus:border-primary focus:ring-2 focus:ring-primary/15 transition-all duration-200"
                    placeholder="0" />
                </label>
              </div>
              <button data-testid="debt-add-item-btn" onClick={add}
                className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-full text-sm font-semibold transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-primary/25">
                <Plus size={15} /> Legg til
              </button>
            </div>

            <div className="mt-8 space-y-3">
              <AnimatePresence initial={false}>
                {debts.map((d) => (
                  <motion.div
                    key={d.id}
                    data-testid="debt-item-row"
                    layout
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, x: -24 }}
                    transition={{ duration: 0.3 }}
                    className="flex items-center justify-between gap-3 bg-background border border-border rounded-xl px-4 py-3"
                  >
                    <div>
                      <p className="text-sm font-semibold">{d.name}</p>
                      <p className="text-xs font-mono text-muted-foreground">{fmt.format(d.balance)} kr · {d.rate} %</p>
                    </div>
                    <button data-testid={`debt-remove-${d.name}`} onClick={() => setDebts((arr) => arr.filter((x) => x.id !== d.id))}
                      aria-label={`Fjern ${d.name}`}
                      className="text-muted-foreground hover:text-destructive transition-colors">
                      <Trash2 size={15} />
                    </button>
                  </motion.div>
                ))}
              </AnimatePresence>
              {debts.length === 0 && (
                <p className="text-sm text-muted-foreground">Ingen gjeld lagt til ennå.</p>
              )}
            </div>
          </div>

          <div className="lg:col-span-7 flex flex-col gap-6">
            <div data-testid="debt-total-summary" className="bg-surface border border-border rounded-3xl p-6 lg:p-8">
              <p className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Total gjeld</p>
              <p className="mt-2 font-serif font-bold text-4xl">{fmt.format(total)} kr</p>
            </div>

            <div className="bg-surface border border-border rounded-3xl p-6 lg:p-8 flex-1">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <h3 className="font-serif font-bold text-xl">Anbefalt nedbetalingsrekkefølge</h3>
                <div className="flex gap-2" data-testid="debt-strategy-toggle">
                  {[
                    { id: "snoeball", label: "Snøballmetoden" },
                    { id: "skred", label: "Skredmetoden" },
                  ].map((s) => (
                    <button key={s.id} data-testid={`debt-strategy-${s.id}`} onClick={() => setStrategy(s.id)}
                      className={`px-4 py-2 rounded-full text-xs font-semibold transition-all duration-200 ${
                        strategy === s.id ? "bg-primary text-primary-foreground" : "bg-accent text-accent-foreground hover:bg-primary/15"
                      }`}>
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>
              <p className="mt-3 text-xs text-muted-foreground">
                {strategy === "snoeball"
                  ? "Betal minstebeløp på alt, og legg all ekstra kapasitet på den minste gjelden først — raske seire holder motivasjonen oppe."
                  : "Betal minstebeløp på alt, og legg all ekstra kapasitet på gjelden med høyest rente — matematisk billigst over tid."}
              </p>
              <ol className="mt-6 space-y-3">
                {order.map((d, i) => (
                  <li key={d.id} className="flex items-center gap-4 bg-background border border-border rounded-xl px-4 py-3">
                    <span className="w-7 h-7 rounded-full bg-accent text-accent-foreground flex items-center justify-center font-mono text-xs font-semibold shrink-0">
                      {i + 1}
                    </span>
                    <div className="flex-1">
                      <p className="text-sm font-semibold">{d.name}</p>
                    </div>
                    <p className="text-sm font-mono text-muted-foreground">{fmt.format(d.balance)} kr</p>
                  </li>
                ))}
                {order.length === 0 && <p className="text-sm text-muted-foreground">Legg til gjeld for å se rekkefølgen.</p>}
              </ol>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
